import { Accordion, Box, Flex, Text } from "@chakra-ui/react";
import { kebabCase } from "lodash";
import { useRouter } from "next/router";
import { Fragment, useEffect, useMemo, useState, VFC } from "react";

import { groupedConfig, NavItem } from "./config";
import { NavFolder } from "./nav-folder";
import { NavLink } from "./nav-link";

import { User } from "api/get-user";
import { CTALinks } from "components/cta-links";
import docsManifest from "config/docs-manifest.json";

const subNavLeftPadding = 6;

// Private destinations and sources are auto-populated from Sanity, so their
// `private: true` status can't live in `nav/config.ts`. Instead, we look them
// up in the manifest at merge time so entitled users still see them in the
// sidebar and everyone else does not.
const privatePagesSet = new Set<string>(docsManifest.privatePages);

type Props = {
  destinations?: Array<any> | undefined;
  sources?: Array<any> | undefined;
  user?: User;
  toggle?: () => void;
  label?: string;
};

export const Nav: VFC<Readonly<Props>> = (props) => {
  const router = useRouter();
  const [accordionIndex, setAccordionIndex] = useState<number[]>([]);

  const handleAccordionChange = (expandedIndexes: number[]) => {
    setAccordionIndex(expandedIndexes);
  };

  // The index of the currently active top-level nav accordion item,
  // computed across the flattened visible *folder* items from all groups.
  // Direct links (items without `nav`) are not accordion items and don't count.
  const currentRouteNavIndex = useMemo(() => {
    const currentPath = `/${router.pathname.split("/")[1]}`;
    const visibleFolders = groupedConfig
      .flatMap((group) => group.items)
      .filter((item) => item.nav && (!item.private || props.user?.private));
    return visibleFolders.findIndex(
      (item) => (item.path || `/${kebabCase(item.label)}`) === currentPath,
    );
  }, [router.pathname, props.user?.private]);

  useEffect(() => {
    if (currentRouteNavIndex !== -1) {
      setAccordionIndex([currentRouteNavIndex]);
    }
  }, [currentRouteNavIndex]);

  const processNav = (nav: NavItem[], depth: number, pathPrefix = "") => {
    return nav.map((item) => processNavItem(item, depth, pathPrefix));
  };

  const processNavItem = (item: NavItem, depth: number, pathPrefix = "") => {
    const path = `${pathPrefix}${item.path ?? `/${kebabCase(item.label)}`}`;

    if (item.private && !props.user?.private) {
      return null;
    }

    const isGroupLabel = depth === 2 && item.nav?.length;

    if (isGroupLabel) {
      return (
        <Box as="li" listStyleType="none" key={item.label}>
          <Text
            as="span"
            display="block"
            textStyle="mono"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="0.08em"
            textTransform="uppercase"
            color="text.tertiary"
            ml={subNavLeftPadding}
            mt={1}
            pt={4}
            pb={2}
            borderTop="1px"
            borderColor="base.divider"
          >
            {item.label}
          </Text>
          <Box as="ul" listStyleType="none">
            {item.nav && processNav(item.nav, depth + 1, pathPrefix)}
          </Box>
        </Box>
      );
    }

    const isActive =
      router.asPath === path ||
      (router.pathname === "/getting-started/welcome" &&
        path === "/getting-started/welcome");

    return (
      <Fragment key={item.label}>
        <Box as="li">
          <NavLink
            item={item}
            isActive={isActive}
            path={path}
            subNavLeftPadding={subNavLeftPadding + Math.max(0, depth - 3) * 4}
            toggle={props.toggle}
          />
          {item.nav && (
            <Box as="ul" listStyleType="none">
              {processNav(item.nav, depth + 1, pathPrefix)}
            </Box>
          )}
        </Box>
        {item.dividerAfter && (
          <Box
            as="li"
            mx={subNavLeftPadding}
            my={1}
            borderTop="1px"
            borderColor="base.divider"
          />
        )}
      </Fragment>
    );
  };

  /** Enrich a NavItem with dynamic sources/destinations data. */
  const enrichItem = (item: NavItem): NavItem => {
    if (item.label === "Sources") {
      const filteredSources = props.sources
        ?.filter(
          ({ status }) => status === "released" || status === "alpha",
        )
        .map(({ name, slug }) => ({
          label: name,
          path: `/${slug.replace("-source", "")}`,
        }))
        .filter(
          ({ path }) =>
            !privatePagesSet.has(`/sources${path}`) || props.user?.private,
        );

      return {
        ...item,
        nav: [...(item.nav ?? []), ...(filteredSources ?? [])],
      };
    }

    if (item.label === "Destinations") {
      const filteredDestinations = props.destinations
        ?.filter(
          ({ slug, status, approvalRequirement }) =>
            slug !== "hightouch-personalization-api" &&
            (status === "released" || status === "alpha") &&
            approvalRequirement !== "to_view",
        )
        .map(({ name, slug }) => {
          let slugOverride = undefined;
          let nameOverride = undefined;

          if (slug === "twitter") {
            slugOverride = "x";
          }
          if (slug === "webhook-v2") {
            slugOverride = "http-request";
          }
          if (slug === "hightouch-personalization-api") {
            slugOverride = "personalization-api";
          }
          if (name === "Google Sheets - User Account") {
            nameOverride = "Google Sheets";
          }
          return {
            label: nameOverride ? nameOverride : name,
            path: `/${slugOverride
                ? slugOverride
                : slug
                  .replace("-destination", "")
                  .replace("facebook", "meta")
              }`,
          };
        })
        .filter(
          ({ path }) =>
            !privatePagesSet.has(`/destinations${path}`) ||
            props.user?.private,
        );
      return {
        ...item,
        nav: [
          ...(item.nav ?? []).filter(({ path }) => path === "/overview"),
          ...[
            ...(item.nav ?? []).filter(({ path }) => path !== "/overview"),
            ...(filteredDestinations ?? []),
          ].sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
          ),
        ],
      };
    }

    return item;
  };

  /** Build grouped config with enriched items and private filtering applied. */
  const navGroups = useMemo(
    () =>
      groupedConfig
        .map((group) => ({
          ...group,
          items: group.items
            .filter((item) => !item.private || props.user?.private)
            .map(enrichItem),
        }))
        .filter((group) => group.items.length > 0),
    [props.destinations, props.sources, props.user?.private],
  );

  const content = useMemo(() => {
    return navGroups.map((group, groupIndex) => (
      <Fragment key={`${group.heading ?? "ungrouped"}-${groupIndex}`}>
        {group.heading && (
          <Box
            pt={groupIndex === 0 ? 0 : 7}
            pb={1}
            pl={2}
          >
            <Text
              textStyle="mono"
              fontSize="xs"
              fontWeight="semibold"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="text.tertiary"
            >
              {group.heading}
            </Text>
          </Box>
        )}
        {!group.heading && groupIndex > 0 && (
          <Box
            mx={4}
            my={2}
            borderTop="1px"
            borderStyle="dashed"
            borderColor="base.divider"
          />
        )}
        <Box as="ul" listStyleType="none" pl={group.heading ? 2 : 0}>
          {group.items.map((item) => {
            const isFolder = Boolean(item.nav);
            const path = item.path ?? `/${kebabCase(item.label)}`;

            if (isFolder) {
              const isActive = router.pathname.startsWith(path);
              return (
                <Box as="li" key={item.label}>
                  <NavFolder isActive={isActive} item={item}>
                    {item.nav && processNav(item.nav, 2, path)}
                  </NavFolder>
                </Box>
              );
            }

            const linkPath = item.href ?? path;
            const isActive =
              router.asPath === linkPath || router.pathname === path;
            return (
              <Box as="li" key={item.label} fontSize="sm" opacity={0.8}>
                <NavLink
                  item={item}
                  isActive={isActive}
                  path={linkPath}
                  subNavLeftPadding={2}
                  toggle={props.toggle}
                />
              </Box>
            );
          })}
        </Box>
      </Fragment>
    ));
  }, [navGroups, router.pathname, router.asPath, processNav, props.toggle]);

  return (
    <Flex
      as="nav"
      aria-label={props.label ?? "Documentation"}
      direction="column"
      width="100%"
      p={{ base: 2, md: 4 }}
    >
      <Accordion
        allowToggle
        index={accordionIndex}
        onChange={handleAccordionChange}
      >
        {content}
      </Accordion>

      <CTALinks
        includeChangelog
        hideSecondary
        primary="signup"
        display={{ base: "flex", md: "none" }}
        mt={8}
        mx={6}
      />
    </Flex>
  );
};
